/**
 * Schema.org JSON-LD Strukturdaten für SEO
 * Implementiert LocalBusiness, Organization und SportsActivityLocation
 */

// Basis-Informationen
const businessInfo = {
  name: "Fußballtraining Aktas",
  alternateName: "Personal Fußballtraining",
  description: "Persönliches Fußballtraining für Kinder, Jugendliche und Erwachsene. Individuelle Förderung, Techniktraining, Athletik und Fußballcamps für jedes Leistungsniveau.",
  url: "https://personal-fussballtraining.de",
  logo: "https://personal-fussballtraining.de/logo.png",
  image: "https://personal-fussballtraining.de/og-image.jpg",
  telephone: "+49 174 1944996",
  email: "kontakt@fussballtraining-aktas.de",
  address: {
    streetAddress: "Unterm Hestenberg 2",
    addressLocality: "Meinerzhagen",
    postalCode: "58540",
    addressRegion: "Nordrhein-Westfalen",
    addressCountry: "DE"
  },
  geo: {
    latitude: "51.1067",
    longitude: "7.6333"
  },
  priceRange: "€€",
  founder: "Yasin Aktas",
  foundingDate: "2020",
  sameAs: [
    "https://www.instagram.com/fussballtraining_aktas/",
    "https://www.tiktok.com/@fussballtraining_aktas",
    "https://wa.me/491741944996"
  ]
};

/**
 * LocalBusiness Schema
 */
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${businessInfo.url}/#localbusiness`,
    name: businessInfo.name,
    alternateName: businessInfo.alternateName,
    description: businessInfo.description,
    url: businessInfo.url,
    logo: businessInfo.logo,
    image: businessInfo.image,
    telephone: businessInfo.telephone,
    email: businessInfo.email,
    priceRange: businessInfo.priceRange,
    address: {
      "@type": "PostalAddress",
      streetAddress: businessInfo.address.streetAddress,
      addressLocality: businessInfo.address.addressLocality,
      postalCode: businessInfo.address.postalCode,
      addressRegion: businessInfo.address.addressRegion,
      addressCountry: businessInfo.address.addressCountry
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: businessInfo.geo.latitude,
      longitude: businessInfo.geo.longitude
    },
    sameAs: businessInfo.sameAs,
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "20:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Saturday", "Sunday"],
        opens: "09:00",
        closes: "18:00"
      }
    ],
    areaServed: [
      {
        "@type": "City",
        name: "Meinerzhagen"
      },
      {
        "@type": "City",
        name: "Plettenberg"
      },
      {
        "@type": "City",
        name: "Attendorn"
      },
      {
        "@type": "City",
        name: "Drolshagen"
      },
      {
        "@type": "State",
        name: "Nordrhein-Westfalen"
      }
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * Organization Schema
 */
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${businessInfo.url}/#organization`,
    name: businessInfo.name,
    alternateName: businessInfo.alternateName,
    url: businessInfo.url,
    logo: {
      "@type": "ImageObject",
      url: businessInfo.logo,
      width: "512",
      height: "512"
    },
    image: businessInfo.image,
    description: businessInfo.description,
    email: businessInfo.email,
    telephone: businessInfo.telephone,
    address: {
      "@type": "PostalAddress",
      streetAddress: businessInfo.address.streetAddress,
      addressLocality: businessInfo.address.addressLocality,
      postalCode: businessInfo.address.postalCode,
      addressRegion: businessInfo.address.addressRegion,
      addressCountry: businessInfo.address.addressCountry
    },
    founder: {
      "@type": "Person",
      name: businessInfo.founder
    },
    foundingDate: businessInfo.foundingDate,
    sameAs: businessInfo.sameAs,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: businessInfo.telephone,
      email: businessInfo.email,
      contactType: "customer service",
      availableLanguage: ["German"]
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * SportsActivityLocation Schema
 */
export const SportsActivityLocationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "SportsActivityLocation",
    "@id": `${businessInfo.url}/#sportsactivitylocation`,
    name: businessInfo.name,
    description: "Fußballtraining und Fußballschule für individuelle Förderung von Kindern, Jugendlichen und Erwachsenen. Techniktraining, Athletik und Camps.",
    url: businessInfo.url,
    image: businessInfo.image,
    telephone: businessInfo.telephone,
    email: businessInfo.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: businessInfo.address.streetAddress,
      addressLocality: businessInfo.address.addressLocality,
      postalCode: businessInfo.address.postalCode,
      addressRegion: businessInfo.address.addressRegion,
      addressCountry: businessInfo.address.addressCountry
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: businessInfo.geo.latitude,
      longitude: businessInfo.geo.longitude
    },
    sport: "Soccer",
    sameAs: businessInfo.sameAs,
    amenityFeature: [
      {
        "@type": "LocationFeatureSpecification",
        name: "Personal Training",
        value: true
      },
      {
        "@type": "LocationFeatureSpecification",
        name: "Group Training",
        value: true
      },
      {
        "@type": "LocationFeatureSpecification",
        name: "Football Camps",
        value: true
      },
      {
        "@type": "LocationFeatureSpecification",
        name: "Youth Academy",
        value: true
      }
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * Service Schema für Fußballtraining
 */
export const ServiceSchema = ({ serviceName, description, price }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: serviceName,
    description: description,
    provider: {
      "@type": "LocalBusiness",
      name: businessInfo.name,
      url: businessInfo.url
    },
    areaServed: {
      "@type": "State",
      name: "Nordrhein-Westfalen"
    },
    ...(price && {
      offers: {
        "@type": "Offer",
        price: price,
        priceCurrency: "EUR"
      }
    })
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * Event Schema für Camps
 */
export const EventSchema = ({ name, description, startDate, endDate, location, price, image }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: name,
    description: description,
    startDate: startDate,
    endDate: endDate,
    location: {
      "@type": "Place",
      name: location || "Meinerzhagen",
      address: {
        "@type": "PostalAddress",
        addressLocality: businessInfo.address.addressLocality,
        addressRegion: businessInfo.address.addressRegion,
        addressCountry: businessInfo.address.addressCountry
      }
    },
    organizer: {
      "@type": "Organization",
      name: businessInfo.name,
      url: businessInfo.url
    },
    offers: {
      "@type": "Offer",
      price: price,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: `${businessInfo.url}/camps`
    },
    image: image,
    sport: "Soccer"
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * WebSite Schema für Sitelinks-Suchfeld
 */
export const WebSiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${businessInfo.url}/#website`,
    name: businessInfo.name,
    alternateName: businessInfo.alternateName,
    url: businessInfo.url,
    description: businessInfo.description,
    publisher: {
      "@id": `${businessInfo.url}/#organization`
    },
    inLanguage: "de-DE"
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * BreadcrumbList Schema
 */
export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * Kombinierte Schemas für die Startseite
 */
export default function SchemaMarkup() {
  return (
    <>
      <LocalBusinessSchema />
      <OrganizationSchema />
      <SportsActivityLocationSchema />
      <WebSiteSchema />
    </>
  );
}
